import React, { useState, useEffect } from 'react';
import { Pencil, Trash2 } from 'lucide-react';
import { PaymentTypeResponse, PaymentTypeCreate } from '../types';
import { confirmAsync } from '../utils/toast';

interface PaymentTypeManagementProps {
  apiBaseUrl: string;
}

const PaymentTypeManagement: React.FC<PaymentTypeManagementProps> = ({ apiBaseUrl }) => {
  const [paymentTypes, setPaymentTypes] = useState<PaymentTypeResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);

  const [formData, setFormData] = useState<PaymentTypeCreate>({
    name: '',
    display_name: '',
    description: '',
    icon: '',
    display_order: 0,
    is_active: true
  });

  useEffect(() => {
    fetchPaymentTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPaymentTypes = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('access_token');
      const url = `${apiBaseUrl}/admin/payment-types`;
      console.log('Fetching payment types from:', url);
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch payment types');
      }

      const data = await response.json();
      setPaymentTypes(data);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setError(null);
      const token = localStorage.getItem('access_token');
      const url = editingId
        ? `${apiBaseUrl}/admin/payment-types/${editingId}`
        : `${apiBaseUrl}/admin/payment-types`;

      const response = await fetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to save payment type');
      }

      await fetchPaymentTypes();
      resetForm();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const handleEdit = (paymentType: PaymentTypeResponse) => {
    setEditingId(paymentType.id);
    setFormData({
      name: paymentType.name,
      display_name: paymentType.display_name,
      description: paymentType.description || '',
      icon: paymentType.icon || '',
      display_order: paymentType.display_order,
      is_active: paymentType.is_active
    });
    setShowAddForm(true);
  };

  const handleDelete = async (id: number) => {
    const confirmed = await confirmAsync('Are you sure you want to delete this payment type? This will fail if any artist fairs are using it.');
    if (!confirmed) return;

    try {
      setError(null);
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${apiBaseUrl}/admin/payment-types/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to delete payment type');
      }

      await fetchPaymentTypes();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      display_name: '',
      description: '',
      icon: '',
      display_order: 0,
      is_active: true
    });
    setEditingId(null);
    setShowAddForm(false);
  };

  const handleToggleActive = async (paymentType: PaymentTypeResponse) => {
    try {
      setError(null);
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${apiBaseUrl}/admin/payment-types/${paymentType.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          is_active: !paymentType.is_active
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to update payment type');
      }

      await fetchPaymentTypes();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  if (loading && paymentTypes.length === 0) {
    return <div className="text-center py-8">Loading payment types...</div>;
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Payment Type Management</h2>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors"
        >
          {showAddForm ? 'Cancel' : 'Add Payment Type'}
        </button>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {showAddForm && (
        <div className="bg-white p-6 rounded-lg shadow-md mb-6">
          <h3 className="text-lg font-semibold mb-4">
            {editingId ? 'Edit Payment Type' : 'Add New Payment Type'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                  Name (lowercase, no spaces) <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
                  required
                  placeholder="e.g., apple_pay"
                />
              </div>

              <div>
                <label htmlFor="display_name" className="block text-sm font-medium text-gray-700 mb-1">
                  Display Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="display_name"
                  value={formData.display_name}
                  onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
                  required
                  placeholder="e.g., Apple Pay"
                />
              </div>

              <div>
                <label htmlFor="icon" className="block text-sm font-medium text-gray-700 mb-1">
                  Icon (emoji)
                </label>
                <input
                  type="text"
                  id="icon"
                  value={formData.icon}
                  onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
                  placeholder="e.g., 🍎"
                  maxLength={10}
                />
              </div>

              <div>
                <label htmlFor="display_order" className="block text-sm font-medium text-gray-700 mb-1">
                  Display Order
                </label>
                <input
                  type="number"
                  id="display_order"
                  value={formData.display_order}
                  onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
                  min="0"
                />
              </div>
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
                rows={2}
                placeholder="Brief description of this payment type"
              />
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="h-4 w-4 text-secondary focus:ring-primary border-gray-300 rounded"
              />
              <label htmlFor="is_active" className="ml-2 block text-sm text-gray-700">
                Active (visible to users)
              </label>
            </div>

            <div className="flex gap-3">
              <button
                type="submit"
                className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors"
              >
                {editingId ? 'Update' : 'Create'} Payment Type
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Icon
              </th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Display Name
              </th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Name
              </th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Description
              </th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Order
              </th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {paymentTypes.map((paymentType) => (
              <tr key={paymentType.id} className={!paymentType.is_active ? 'bg-gray-50' : ''}>
                <td className="px-3 py-3 whitespace-nowrap text-2xl">
                  {paymentType.icon}
                </td>
                <td className="px-3 py-3 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">{paymentType.display_name}</div>
                </td>
                <td className="px-3 py-3 whitespace-nowrap">
                  <div className="text-sm text-gray-500">{paymentType.name}</div>
                </td>
                <td className="px-3 py-3">
                  <div className="text-sm text-gray-500 max-w-xs truncate">
                    {paymentType.description || '-'}
                  </div>
                </td>
                <td className="px-3 py-3 whitespace-nowrap">
                  <div className="text-sm text-gray-500">{paymentType.display_order}</div>
                </td>
                <td className="px-3 py-3 whitespace-nowrap">
                  <button
                    onClick={() => handleToggleActive(paymentType)}
                    className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      paymentType.is_active
                        ? 'bg-teal-100 text-primary-dark'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {paymentType.is_active ? 'Active' : 'Inactive'}
                  </button>
                </td>
                <td className="px-3 py-3 whitespace-nowrap text-sm font-medium">
                  <button
                    onClick={() => handleEdit(paymentType)}
                    className="text-secondary hover:text-ink transition-colors mr-3"
                    aria-label="Edit payment type"
                    title="Edit"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(paymentType.id)}
                    className="text-red-600 hover:text-red-800 transition-colors"
                    aria-label="Delete payment type"
                    title="Delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {paymentTypes.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            No payment types found. Add one to get started!
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentTypeManagement;
